import React from "react";
import {
  Mail,
  BarChart3,
  Plus,
  Settings,
  FileText,
  Users,
  Calendar,
} from "lucide-react";
import PageHeader from "../../../../components/marketing/cloud-dashboard/header";
import { useNavigate } from "react-router-dom";

const EmailDashboard: React.FC = () => {
  // Mock data for email campaigns
  const navigate = useNavigate();
  const campaigns = [
    {
      id: 1,
      name: "Welcome Series",
      sent: "12.5K",
      openRate: "42%",
      clickRate: "8.2%",
      status: "active",
    },
    {
      id: 2,
      name: "Product Announcement",
      sent: "8.7K",
      openRate: "38%",
      clickRate: "6.5%",
      status: "completed",
    },
    {
      id: 3,
      name: "Weekly Newsletter",
      sent: "23.1K",
      openRate: "45%",
      clickRate: "9.1%",
      status: "scheduled",
    },
  ];

  const stats = [
    { label: "Sent", value: "892K", change: "+12.4%" },
    { label: "Opened", value: "24.8%", change: "+2.1%" },
    { label: "Clicked", value: "3.2%", change: "+0.8%" },
  ];
  function onBack() {
    navigate("/cpas-dashboard");
  }

  function onActionButtonClick() {
    navigate("/campaigns/create");
  }

  return (
    <>
      <PageHeader
        title="Email Marketing"
        subtitle="Professional email campaigns with advanced automation"
        icon={<Mail className="text-purple-600 dark:text-purple-400" />}
        onBack={onBack}
        actionButtonText="Create Campaign"
        onActionButtonClick={onActionButtonClick}
        actionButtonIcon={<Plus size={16} />}
      />

      <div className="max-w-5xl mx-auto ">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-500/15 rounded-full mb-4">
            <Mail className="w-8 h-8 text-red-500 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Email Marketing
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Professional email campaigns with advanced automation
          </p>
          <div className="inline-flex items-center space-x-2 px-3 py-1 bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 rounded-full text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Active & Connected</span>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <h3 className="text-2xl font-bold mb-5 text-gray-900 dark:text-gray-100">Recent Performance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
            {stats.map((stat, index) => (
              <div key={index}>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{stat.label}</p>
                <div className="flex items-end justify-between">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {stat.value}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Campaigns */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Recent Campaigns
                </h2>
                <button className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                  View all
                </button>
              </div>
              <div className="space-y-4">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between p-4 border border-gray-100 dark:border-gray-800 rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {campaign.name}
                      </h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          Sent: {campaign.sent}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          Open: {campaign.openRate}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          Click: {campaign.clickRate}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        campaign.status === "active"
                          ? "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-400"
                          : campaign.status === "completed"
                          ? "bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-400"
                          : "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-400"
                      }`}
                    >
                      {campaign.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button className="flex items-center space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
                  <FileText size={20} className="text-blue-600 dark:text-blue-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      Create Template
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Design a new email template
                    </div>
                  </div>
                </button>
                <button className="flex items-center space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
                  <Users size={20} className="text-green-600 dark:text-green-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      Manage Lists
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Create and segment email lists
                    </div>
                  </div>
                </button>
                <button className="flex items-center space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
                  <BarChart3 size={20} className="text-purple-600 dark:text-purple-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      View Analytics
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Check performance metrics
                    </div>
                  </div>
                </button>
                <button className="flex items-center space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
                  <Calendar size={20} className="text-orange-600 dark:text-orange-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">Schedule</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      Plan future campaigns
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 w-full">
            {/* Features */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Key Features
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Drag & drop email builder
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    A/B testing capabilities
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Advanced segmentation
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Automated drip campaigns
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Responsive design testing
                  </span>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Resources
              </h3>
              <div className="space-y-3">
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    📖 Documentation
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Complete setup guide
                  </div>
                </button>
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    🔧 API Reference
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Developer integration docs
                  </div>
                </button>
                <button className="w-full text-left p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    💡 Best Practices
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Tips for better engagement
                  </div>
                </button>
              </div>
            </div>

            {/* Support */}
            <div className="bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-2">
                Need Support?
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-400 mb-4">
                Our team is here to help you get started
              </p>
              <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailDashboard;
